import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import { maintenanceAPI } from "../../utils/api";

const UserMaintenanceRequests = ({ showHistory = false }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    satisfactionRating: 5,
    satisfactionFeedback: "",
  });

  useEffect(() => {
    fetchRequests();
  }, [showHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await maintenanceAPI.getMyRequests();
      let filteredRequests = response.data;

      if (showHistory) {
        // Show completed/closed requests
        filteredRequests = response.data.filter((req) =>
          ["resolved", "closed", "rejected"].includes(req.status)
        );
      } else {
        // Show active requests
        filteredRequests = response.data.filter(
          (req) => !["closed"].includes(req.status)
        );
      }

      setRequests(filteredRequests);
      setError("");
    } catch (error) {
      console.error("Error fetching maintenance requests:", error);
      setError("Failed to load maintenance requests");
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialog(true);
  };

  const handleProvideFeedback = (request) => {
    setSelectedRequest(request);
    setFeedbackDialog(true);
    setFeedbackForm({
      satisfactionRating: 5,
      satisfactionFeedback: "",
    });
  };

  const handleSubmitFeedback = async () => {
    try {
      await maintenanceAPI.closeRequest(selectedRequest.id, feedbackForm);
      setFeedbackDialog(false);
      setSelectedRequest(null);
      await fetchRequests();
      setError("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setError("Failed to submit feedback");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "assigned":
        return "info";
      case "in_progress":
        return "primary";
      case "resolved":
        return "success";
      case "rejected":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "Pending Assignment";
      case "assigned":
        return "Worker Assigned";
      case "in_progress":
        return "In Progress";
      case "resolved":
        return "Completed";
      case "rejected":
        return "Declined";
      case "closed":
        return "Closed";
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          {showHistory ? "Request History" : "Active Requests"}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchRequests} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {showHistory
              ? "No completed requests found"
              : "No active maintenance requests found"}
          </Typography>
          {!showHistory && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Submit a new request using the "Submit Request" tab
            </Typography>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token #</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Worker</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {request.tokenNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {request.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.location}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.priority}
                      color={getPriorityColor(request.priority)}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(request.status)}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.assignedWorkerName || "Not assigned"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewRequest(request)}
                      title="View Details"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {request.status === "resolved" && !request.closed && (
                      <Button
                        size="small"
                        onClick={() => handleProvideFeedback(request)}
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        Provide Feedback
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Request Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Request Details - {selectedRequest?.tokenNumber}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Category:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, textTransform: "capitalize" }}
                  >
                    {selectedRequest.category}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Priority:
                  </Typography>
                  <Chip
                    label={selectedRequest.priority}
                    color={getPriorityColor(selectedRequest.priority)}
                    sx={{ textTransform: "capitalize", mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Location:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedRequest.location}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Description:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedRequest.description}
                  </Typography>
                </Grid>

                {selectedRequest.assignedWorkerName && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Assigned Worker Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="subtitle2">Worker:</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedRequest.assignedWorkerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="subtitle2">Contact:</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedRequest.assignedWorkerPhone ||
                          selectedRequest.assignedWorkerEmail}
                      </Typography>
                    </Grid>
                  </>
                )}

                {selectedRequest.scheduledDate && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ScheduleIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="subtitle2">
                        Scheduled Date:
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {new Date(selectedRequest.scheduledDate).toLocaleString()}
                    </Typography>
                  </Grid>
                )}

                {selectedRequest.adminNotes && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <NotesIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="subtitle2">Admin Notes:</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedRequest.adminNotes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialog}
        onClose={() => setFeedbackDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FeedbackIcon sx={{ mr: 1 }} />
            Provide Feedback - {selectedRequest?.tokenNumber}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              How satisfied are you with the completed work?
            </Typography>
            <TextField
              fullWidth
              select
              label="Satisfaction Rating"
              value={feedbackForm.satisfactionRating}
              onChange={(e) =>
                setFeedbackForm({
                  ...feedbackForm,
                  satisfactionRating: parseInt(e.target.value),
                })
              }
              sx={{ mb: 3 }}
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <option key={rating} value={rating}>
                  {rating} -{" "}
                  {rating === 5
                    ? "Excellent"
                    : rating === 4
                    ? "Good"
                    : rating === 3
                    ? "Average"
                    : rating === 2
                    ? "Poor"
                    : "Very Poor"}
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Comments"
              value={feedbackForm.satisfactionFeedback}
              onChange={(e) =>
                setFeedbackForm({
                  ...feedbackForm,
                  satisfactionFeedback: e.target.value,
                })
              }
              placeholder="Please share your feedback about the quality of work, timeliness, and overall experience..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            color="primary"
            disabled={!feedbackForm.satisfactionFeedback.trim()}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserMaintenanceRequests;
